import type { ReactNode } from 'react';
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  Circle,
  Clock,
  RotateCcw,
  Save,
  Send,
  Undo2,
  type LucideIcon,
} from 'lucide-react';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import type {
  TaskSignatureBypassResponse,
  TaskSignatureResponse,
  TaskStatus,
} from '../../services/api/types.gen';
import { STATUS_DISPLAY_LABELS, getStatusBadgeVariant } from './taskStatus';
import {
  computeSignatureSummary,
  signerName,
} from '../signing/signatureSummary';
import { SignatureSummaryTooltip } from '../signing/SignatureSummaryTooltip';
import { formatSignatureDate } from '../signing/signatureDisplayDate';

export interface TaskHeaderSecondaryAction {
  label: string;
  icon: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
}

interface TaskHeaderControlsProps {
  statusItems?: ReactNode;
  primaryActions?: ReactNode;
  secondaryActions?: TaskHeaderSecondaryAction[];
  className?: string;
}

const joinClassNames = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

const STATUS_ICON_BY_STATUS: Record<TaskStatus, LucideIcon> = {
  not_started: Circle,
  saved: Save,
  returned_for_review: Undo2,
  submitted_with_signature: CheckCircle,
  submitted: Send,
  pending_clinician_signature: Clock,
  completed: CheckCircle,
  exported: CheckCircle,
  reopened: RotateCcw,
  sent_to_physician: Send,
  returned_with_signature: CheckCircle,
  sent_no_signature_required: CheckCircle,
};

const STATUS_TOOLTIP_TEXT: Record<TaskStatus, string> = {
  not_started: 'Work has not started yet.',
  saved: 'Draft changes have been saved but not submitted.',
  returned_for_review:
    'Returned by QA for corrections. Edit, then sign or submit again.',
  submitted_with_signature: 'Submitted with an active signature.',
  submitted: 'Submitted for review and waiting for signature.',
  pending_clinician_signature:
    'Waiting for the clinician signature needed to complete this task.',
  completed: 'All required signatures or approvals are complete.',
  exported: 'Completed and exported.',
  reopened:
    'Reopened for editing after completion. Edits will move it back to Saved.',
  sent_to_physician: 'Sent to the physician and waiting for return.',
  returned_with_signature: 'Returned with a physician signature.',
  sent_no_signature_required:
    'Sent — this order type does not require a physician signature.',
};

interface TaskWorkflowStatusBadgeProps {
  status: TaskStatus;
  /**
   * Active (non-invalidated) signatures. When provided, the signed/awaiting
   * summary is folded into this pill's tooltip instead of standalone pills
   * (ROG-2842), keeping the task header compact.
   */
  signatures?: TaskSignatureResponse[];
  /**
   * Forfeited clinician signatures (ROG-3269). Passed through so a bypassed
   * chart's tooltip says so instead of reading as fully signed.
   */
  bypasses?: TaskSignatureBypassResponse[];
}

export function TaskWorkflowStatusBadge({
  status,
  signatures,
  bypasses,
}: TaskWorkflowStatusBadgeProps) {
  const StatusIcon = STATUS_ICON_BY_STATUS[status] ?? AlertCircle;
  const signatureSummary = signatures
    ? computeSignatureSummary(signatures, status, bypasses)
    : null;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            tabIndex={0}
            className='inline-flex rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1'
          >
            <Badge
              variant={getStatusBadgeVariant(status)}
              className='flex items-center gap-1.5 cursor-default text-sm'
            >
              <StatusIcon className='h-3.5 w-3.5' aria-hidden='true' />
              {STATUS_DISPLAY_LABELS[status] ?? status}
            </Badge>
          </div>
        </TooltipTrigger>
        <TooltipContent side='bottom' className='max-w-xs text-xs'>
          <div className='space-y-2'>
            <p>
              {STATUS_TOOLTIP_TEXT[status] ?? 'Current task workflow status.'}
            </p>
            {signatureSummary && (
              <div className='border-t pt-1.5'>
                <SignatureSummaryTooltip summary={signatureSummary} />
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface TaskSignatureStatusBadgeProps {
  signatures: TaskSignatureResponse[];
  taskLabel: 'visit' | 'order' | 'task';
}

export function TaskSignatureStatusBadge({
  signatures,
  taskLabel,
}: TaskSignatureStatusBadgeProps) {
  const hasSignatures = signatures.length > 0;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            tabIndex={0}
            className='inline-flex rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1'
          >
            {hasSignatures ? (
              <Badge
                variant='default'
                className='flex items-center gap-1.5 cursor-default text-sm'
              >
                <CheckCircle className='h-3.5 w-3.5' aria-hidden='true' />
                Signed by {signatures.length}{' '}
                {signatures.length === 1 ? 'person' : 'people'}
              </Badge>
            ) : (
              <Badge
                variant='outline'
                className='flex items-center gap-1.5 cursor-default text-sm'
              >
                <AlertCircle className='h-3.5 w-3.5' aria-hidden='true' />
                Not Signed
              </Badge>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side='bottom' className='max-w-xs text-xs'>
          {hasSignatures ? (
            <div className='space-y-2'>
              <p>
                This {taskLabel} has {signatures.length} active{' '}
                {signatures.length === 1 ? 'signature' : 'signatures'}.
              </p>
              <ul className='space-y-1'>
                {signatures.map(sig => (
                  <li key={sig.id}>
                    <span className='font-medium'>{signerName(sig)}</span>
                    <span className='text-muted-foreground ml-1'>
                      {formatSignatureDate(sig)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            `No active signatures are attached to this ${taskLabel}.`
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function TaskHeaderControls({
  statusItems,
  primaryActions,
  secondaryActions = [],
  className,
}: TaskHeaderControlsProps) {
  const hasPrimaryActions = Boolean(primaryActions);
  const hasSecondaryActions = secondaryActions.length > 0;
  const hasAnyActions = hasPrimaryActions || hasSecondaryActions;

  return (
    <div
      className={joinClassNames(
        'flex min-w-0 items-center justify-end gap-2 flex-wrap',
        className
      )}
    >
      {statusItems && (
        <div
          className={joinClassNames(
            'flex items-center gap-2 flex-wrap justify-end',
            hasAnyActions && 'pr-2 border-r'
          )}
        >
          {statusItems}
        </div>
      )}

      {hasPrimaryActions && (
        <div className='flex items-center gap-2 flex-wrap justify-end'>
          {primaryActions}
        </div>
      )}

      {hasSecondaryActions && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size='sm'
              variant='outline'
              className='flex items-center gap-1.5'
            >
              Actions
              <ChevronDown className='h-3.5 w-3.5' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            {secondaryActions.map(action => (
              <DropdownMenuItem
                key={action.label}
                onSelect={action.onSelect}
                disabled={action.disabled}
              >
                {action.icon}
                {action.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
